// Typed helpers, one per server endpoint. URL builders are pure so they can be unit-tested.
import type { AccountSummary, SessionInfo, ThreadListResponse, ThreadView } from "@shared/api";
import { api } from "./api";

export type ThreadListView = "inbox" | "all" | "unread" | "starred";
export const THREAD_LIST_VIEWS: readonly ThreadListView[] = ["inbox", "unread", "starred", "all"];

export interface ThreadListParams {
  /** The legacy split (inbox, all, unread, starred); ignored when `label` is given. */
  view?: ThreadListView | undefined;
  accountId?: string | undefined;
  /** A Gmail label id (INBOX, STARRED, UNREAD, SENT, a user label…): the inbox-views split. */
  label?: string | undefined;
  unread?: boolean | undefined;
  starred?: boolean | undefined;
  cursor?: string | null | undefined;
  limit?: number | undefined;
}

export const DEFAULT_PAGE_SIZE = 50;

function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export function threadsUrl(params: ThreadListParams): string {
  return `/api/mail/threads${query({
    view: params.label ? undefined : params.view,
    label: params.label,
    unread: params.unread ? 1 : undefined,
    starred: params.starred ? 1 : undefined,
    accountId: params.accountId,
    cursor: params.cursor,
    limit: params.limit ?? DEFAULT_PAGE_SIZE,
  })}`;
}

export function threadUrl(key: string, images: boolean): string {
  return `/api/mail/threads/${encodeURIComponent(key)}${query({ images: images ? 1 : 0 })}`;
}

export function searchUrl(q: string, limit = DEFAULT_PAGE_SIZE): string {
  return `/api/mail/search${query({ q: q.trim(), limit })}`;
}

export function attachmentUrl(messageKey: string, partId: string, inline = false): string {
  const base = `/api/mail/attachments/${encodeURIComponent(messageKey)}/${encodeURIComponent(partId)}`;
  return inline ? `${base}?inline=1` : base;
}

const accountPath = (id: string, action?: string) =>
  `/api/google/accounts/${encodeURIComponent(id)}${action ? `/${action}` : ""}`;

export interface LoginBody {
  password: string;
  totpCode?: string;
}

export interface SetupBody {
  password: string;
  email?: string;
  displayName?: string;
  /** IANA zone the owner confirmed on the setup screen (prefilled from the browser). */
  timeZone?: string;
}

export const endpoints = {
  // Auth
  session: (signal?: AbortSignal) => api<SessionInfo>("/api/auth/session", { signal }),
  setup: (body: SetupBody) => api<SessionInfo>("/api/auth/setup", { method: "POST", body }),
  login: (body: LoginBody) => api<SessionInfo>("/api/auth/login", { method: "POST", body }),
  logout: () => api<unknown>("/api/auth/logout", { method: "POST" }),
  totpEnroll: () =>
    api<{ secret: string; otpauthUrl: string }>("/api/auth/totp/enroll", { method: "POST" }),
  totpConfirm: (code: string) =>
    api<unknown>("/api/auth/totp/confirm", { method: "POST", body: { code } }),
  totpDisable: (password: string) =>
    api<unknown>("/api/auth/totp/disable", { method: "POST", body: { password } }),
  changePassword: (current: string, next: string) =>
    api<unknown>("/api/auth/password", { method: "POST", body: { current, next } }),

  // Google accounts
  accounts: (signal?: AbortSignal) => api<AccountSummary[]>("/api/google/accounts", { signal }),
  connect: (loginHint?: string) =>
    api<{ url: string }>("/api/google/connect", {
      method: "POST",
      body: loginHint ? { loginHint } : {},
    }),
  reconnect: (id: string) => api<{ url: string }>(accountPath(id, "reconnect"), { method: "POST" }),
  disable: (id: string) => api<AccountSummary>(accountPath(id, "disable"), { method: "POST" }),
  enable: (id: string) => api<AccountSummary>(accountPath(id, "enable"), { method: "POST" }),
  remove: (id: string) => api<unknown>(accountPath(id), { method: "DELETE" }),
  heartbeat: (id: string) => api<AccountSummary>(accountPath(id, "heartbeat"), { method: "POST" }),

  // Mail
  threads: (params: ThreadListParams, signal?: AbortSignal) =>
    api<ThreadListResponse>(threadsUrl(params), { signal }),
  thread: (key: string, images: boolean, signal?: AbortSignal) =>
    api<ThreadView>(threadUrl(key, images), { signal }),
  search: (q: string, signal?: AbortSignal) => api<ThreadListResponse>(searchUrl(q), { signal }),
  syncNow: (accountId: string) =>
    api<{ started: boolean }>(`/api/mail/sync/${encodeURIComponent(accountId)}`, {
      method: "POST",
    }),
  acknowledgeGap: (gapId: string) =>
    api<unknown>(`/api/mail/gaps/${encodeURIComponent(gapId)}/acknowledge`, { method: "POST" }),
};
